//Phoneme to audio lookup
// NOTES:
// 1. The base url can be found at process.env.ASSET_ROOT. Provided a simple function below to resolve the URL.
// 2. The files are natively stores at s3://amira-assets/phonemes/. Do not use that url, instead make sure to use the env variable
// 2. The phoneme lookup table has some entries with multiple characters, so the lookup algorithm got a bit complicated.
//    For example, the word "welfare" has the pronunciation "wˈɛlfˌɛɹ". This should result in "w","eh","l","f","ehr".
//    So in this example, we need to match on the single "ɛ" and also on the compound "ɛɹ".


const BASE_URL = process.env.ASSET_ROOT || "s3.amazonaws.com/amira-assets";

export function phonemeneToURL(phon){
  return "https://"+BASE_URL+"/phonemes/"+phon+".mp3";
}

// Quick and dirty code to take a list of phonemes and map it to an audio file name.
export function getAudioForPhonemes(phonemeString,locale='en-us'){
  if(!phonemeString){
    return [];
  }

  let lookupTable = locale == 'en-us' ? phonemeLookup : spanishPhonemeLookup;
  const phonemes = phonemeString.split("");
  let outputNames = [];
  let outIndex = 0;

  for(let i=0;i<phonemes.length;i++){
    //Filter all entries that match the first character
    const matches = lookupTable.filter((row)=>{
      return (row.phoneme.indexOf(phonemes[i]) === 0)
    });

    if(matches.length){
      //If there is one and only one match, we're done
      if(matches.length === 1){
        if(matches[0].phoneme.length == 1){//single char match
          outputNames[outIndex] = phonemeneToURL(matches[0].filename);
          outIndex++;
        }else if(matches[0].phoneme.length > 1 && matches[0].phoneme[1] == phonemes[i+1]){//2 char match
          outputNames[outIndex] = phonemeneToURL(matches[0].filename);
          outIndex++;
          i++; //Increment because we matched two chars.
        }else {
          console.warn("We may have a typo in this phonemeString", phonemeString);
        }
      }else{
        //multiple matches, narrow it down to 1
        let combinedMatches = matches.filter((row)=>{
          return(row.phoneme.indexOf(phonemes[i+1])===1)
        });
        if(combinedMatches.length){
          outputNames[outIndex] = phonemeneToURL(combinedMatches[0].filename);
          outIndex++;
          i++; //Increment index because we matched two chars.
        }else{
          //if no matches on the second pass, then we choose the one with a single char
          const singleCharMatch = matches.filter(({ phoneme }) => phoneme.length == 1)[0];
          if(singleCharMatch && singleCharMatch.filename){
            outputNames[outIndex] = phonemeneToURL(singleCharMatch.filename);
            outIndex++;
          }
        }
      }
    }
  }
  return outputNames;
}

const phonemeLookup = [
  {phoneme: "ɒ", filename: "aa"},
  {phoneme: "æ", filename: "ae"},
  {phoneme: "ʌ", filename: "ah"},
  {phoneme: "ɔ", filename: "ao"},
  {phoneme: "aʊ", filename: "aw"},
  {phoneme: "aɪ", filename: "ay"},
  {phoneme: "b", filename: "b"},
  {phoneme: "ʧ", filename: "ch"},
  {phoneme: "d", filename: "d"},
  {phoneme: "ð", filename: "dh"},
  {phoneme: "ɛ", filename: "eh"},
  {phoneme: "ɝ", filename: "er"},
  {phoneme: "ɚ", filename: "er"},
  {phoneme: "eɪ", filename: "ey"},
  {phoneme: "f", filename: "f"},
  {phoneme: "ɡ", filename: "g"},
  {phoneme: "g", filename: "g"},  //This is not a duplicate. There are two "g" characters in the db
  {phoneme: "h", filename: "h"},
  {phoneme: "ɪ", filename: "ih"},
  {phoneme: "i", filename: "iy"},
  {phoneme: "ʤ", filename: "jh"},
  {phoneme: "k", filename: "k"},
  {phoneme: "l", filename: "l"},
  {phoneme: "m", filename: "m"},
  {phoneme: "n", filename: "n"},
  {phoneme: "ŋ", filename: "ng"},
  {phoneme: "oʊ", filename: "ow"},
  {phoneme: "ɔɪ", filename: "oy"},
  {phoneme: "p", filename: "p"},
  {phoneme: "r", filename: "r"},
  {phoneme: "ɹ", filename: "r"},
  {phoneme: "ɑ", filename: "aar"},
  {phoneme: "ə", filename: "ah"},
  {phoneme: "ɪɹ", filename: "iyr"},
  {phoneme: "ɛɹ", filename: "ehr"},
  {phoneme: "ʊɹ", filename: "yuh"},
  {phoneme: "s", filename: "s"},
  {phoneme: "ʃ", filename: "sh"},
  {phoneme: "t", filename: "t"},
  {phoneme: "θ", filename: "th"},
  {phoneme: "ʊ", filename: "uh"},
  {phoneme: "u", filename: "uw"},
  {phoneme: "v", filename: "v"},
  {phoneme: "w", filename: "w"},
  {phoneme: "j", filename: "y"},
  {phoneme: "z", filename: "z"},
  {phoneme: "ʒ", filename: "zh"},
  {phoneme: "kw", filename: "kw"},
  {phoneme: "ks", filename: "ks"},
  {phoneme: "ɑɹ", filename: "ar"},
  {phoneme: "ɔɹ", filename: "or"},
  {phoneme: "jɝ", filename: "ure"}
];

const spanishPhonemeLookup = [
  {phoneme: "a", filename: "spanish/A"},
  {phoneme: "b", filename: "spanish/B"},
  {phoneme: "β", filename: "spanish/BV"},
  {phoneme: "tʃ", filename: "spanish/CH"},
  {phoneme: "d", filename: "spanish/D"},
  {phoneme: "ð", filename: "spanish/DH"},
  {phoneme: "ɾ", filename: "spanish/DX"},
  {phoneme: "e", filename: "spanish/E"},
  {phoneme: "f", filename: "spanish/F"},
  {phoneme: "ɡ", filename: "spanish/G"},
  {phoneme: "ɣ", filename: "spanish/GH"},
  {phoneme: "h", filename: "spanish/H"},
  {phoneme: "i", filename: "spanish/I"},
  {phoneme: "ʝ", filename: "spanish/J"},
  {phoneme: "ɟʝ", filename: "spanish/JJ"},
  {phoneme: "k", filename: "spanish/K"},
  {phoneme: "l", filename: "spanish/L"},
  {phoneme: "m", filename: "spanish/M"},
  {phoneme: "ɱ", filename: "spanish/MM"},
  {phoneme: "n", filename: "spanish/N"},
  {phoneme: "ŋ", filename: "spanish/NG"},
  {phoneme: "ɲ", filename: "spanish/NN"},
  {phoneme: "o", filename: "spanish/O"},
  {phoneme: "p", filename: "spanish/P"},
  {phoneme: "r", filename: "spanish/RR"},
  {phoneme: "s", filename: "spanish/S"},
  {phoneme: "t", filename: "spanish/T"},
  {phoneme: "θ", filename: "spanish/TH"},
  {phoneme: "u", filename: "spanish/U"},
  {phoneme: "v", filename: "spanish/V"},
  {phoneme: "w", filename: "spanish/W"},
  {phoneme: "x", filename: "spanish/X"},
  {phoneme: "j", filename: "spanish/Y"},
  {phoneme: "ʎ", filename: "spanish/YY"},
  {phoneme: "z", filename: "spanish/Z"}
];